import { useState, useEffect, useCallback } from 'react';
import HiraganaLoader from '../../components/HiraganaLoader';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../app/constants';

// Progress bar component
const ProgressBar = ({ percentage, color = '#9334E9' }) => (
    <div style={{
        width: '100%',
        height: '8px',
        backgroundColor: '#E5E7EB',
        borderRadius: '4px',
        overflow: 'hidden'
    }}>
        <div style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: color,
            borderRadius: '4px',
            transition: 'width 0.3s ease'
        }} />
    </div>
);

// Circular progress component for overall score
const CircularProgress = ({ percentage, total, correct }) => {
    const radius = 70;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
        <div style={{ 
            position: 'relative', 
            width: 'min(180px, 40vw)', 
            height: 'min(180px, 40vw)',
            maxWidth: '180px',
            maxHeight: '180px',
            margin: '0 auto'
        }}>
            <svg 
                width="100%" 
                height="100%" 
                viewBox="0 0 180 180"
                style={{ transform: 'rotate(-90deg)' }}
            >
                {/* Background circle */}
                <circle
                    cx="90"
                    cy="90"
                    r={radius}
                    stroke="#E5E7EB"
                    strokeWidth="6"
                    fill="none"
                />
                {/* Progress circle */}
                <circle
                    cx="90"
                    cy="90"
                    r={radius}
                    stroke="#9334E9"
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                />
            </svg>
            {/* Text overlay */}
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center'
            }}>
                <div style={{
                    fontSize: 'clamp(24px, 6vw, 32px)',
                    fontWeight: '600',
                    color: '#111827'
                }}>
                    {Math.round(percentage)}%
                </div>
                <div style={{
                    fontSize: 'clamp(12px, 3vw, 14px)',
                    color: '#6B7280'
                }}>
                    {correct} of {total} questions
                </div>
            </div>
        </div>
    );
};

function DiagnosticStats() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [diagnosticData, setDiagnosticData] = useState(null);
    const navigate = useNavigate();

    const fetchDiagnosticData = useCallback(async () => {
        try {
            const jwt = localStorage.getItem('jwt');
            if (!jwt) {
                throw new Error("JWT token not found in localStorage");
            }
            const response = await fetch(`${API_URL}/api/v1/panel/diagnostic/analytics`, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setDiagnosticData(data);
        } catch (error) {
            console.error("Error fetching diagnostic data:", error);
            setError("Error fetching diagnostic data");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDiagnosticData();
    }, [fetchDiagnosticData]);

    if (loading) {
        return <HiraganaLoader />;
    }
    
    if (error) {
        return (
            <div style={{
                padding: '20px',
                textAlign: 'center',
                fontFamily: 'Manrope, sans-serif',
                width: '100%',
                minHeight: '100vh',
                backgroundColor: '#FFFFFF',
                maxWidth: '1200px',
                margin: '0 auto'
            }}>
                <h1 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '12px'
                }}>
                    Unable to load diagnostic data
                </h1>
                <p style={{
                    color: '#6B7280',
                    marginBottom: '24px',
                    fontSize: '14px'
                }}>
                    {error}
                </p>
                <button 
                    onClick={() => navigate('/review-personalized/diagnostic')} 
                    style={{
                        padding: '8px 16px',
                        background: '#111827',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}
                >
                    Return to Review
                </button>
            </div>
        );
    }

    return (
        <div style={{
            fontFamily: 'Manrope, sans-serif',
            width: '100vw',
            minHeight: '100vh',
            padding: '0',
            margin: '0',
            backgroundColor: '#FFFFFF'
        }}>
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: 'clamp(12px, 4vw, 20px)'
            }}>
            {/* Header */}
            <div style={{ 
                backgroundColor: '#FFFFFF',
                padding: 'clamp(16px, 4vw, 20px)',
                marginBottom: 'clamp(20px, 6vw, 32px)',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: window.innerWidth <= 768 ? 'stretch' : 'center',
                gap: window.innerWidth <= 768 ? '16px' : '0'
            }}>
                <div>
                    <h1 style={{
                        fontSize: 'clamp(20px, 5vw, 24px)',
                        fontWeight: '600',
                        color: '#111827',
                        margin: '0 0 8px 0'
                    }}>
                        Good job !
                    </h1>
                    <h2 style={{
                        fontSize: 'clamp(18px, 4vw, 20px)',
                        fontWeight: '500',
                        color: '#374151',
                        margin: '0'
                    }}>
                        Performance Summary
                    </h2>
                </div>
                <button
                    onClick={() => {
                        localStorage.setItem('firstTimeStudyPlan', 'true');
                        navigate('/study_plan');
                    }}
                    style={{
                        backgroundColor: '#9334E9',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px 24px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontFamily: 'Manrope, sans-serif'
                    }}
                >
                    Let's Start
                </button>
            </div>

            {/* Email Notice Box - Mobile Only */}
            {window.innerWidth <= 768 && (
                <div style={{
                    backgroundColor: '#FFFFFF',
                    padding: 'clamp(16px, 4vw, 24px)',
                    borderRadius: '16px',
                    border: '1px solid #F3F4F6',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    marginBottom: 'clamp(20px, 5vw, 32px)',
                    textAlign: 'center'
                }}>
                    <p style={{
                        fontSize: '14px',
                        color: '#374151',
                        margin: '0'
                    }}>
                        Check your inbox for a detailed analysis!
                    </p>
                </div>
            )}

            {/* Main Content */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr',
                gap: 'clamp(24px, 6vw, 48px)',
                marginBottom: 'clamp(24px, 6vw, 48px)'
            }}>
                {/* Left Column */}
                <div>
                    {/* Your Score Box */}
                    <div style={{ 
                        marginBottom: 'clamp(20px, 5vw, 32px)',
                        padding: 'clamp(20px, 5vw, 32px)',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '16px',
                        border: '1px solid #F3F4F6',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                    }}>
                        <h3 style={{
                            fontSize: 'clamp(16px, 4vw, 18px)',
                            fontWeight: '500',
                            color: '#111827',
                            marginBottom: 'clamp(16px, 4vw, 24px)',
                            margin: '0 0 clamp(16px, 4vw, 24px) 0'
                        }}>
                            Your score
                        </h3>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <CircularProgress 
                                percentage={diagnosticData.overall_score_percentage}
                                total={diagnosticData.total_questions}
                                correct={diagnosticData.total_correct}
                            />
                        </div>
                    </div>

                    {/* Score Distribution Box */}
                    <div style={{
                        padding: 'clamp(20px, 5vw, 32px)',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '16px',
                        border: '1px solid #F3F4F6',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                    }}>
                        <h3 style={{
                            fontSize: 'clamp(16px, 4vw, 18px)',
                            fontWeight: '500',
                            color: '#111827',
                            marginBottom: 'clamp(16px, 4vw, 24px)',
                            margin: '0 0 clamp(16px, 4vw, 24px) 0'
                        }}>
                            Score distribution
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(20px, 5vw, 32px)' }}>
                            {Object.entries(diagnosticData.section_scores).map(([section, data]) => (
                                <div key={section}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '12px'
                                    }}>
                                        <span style={{
                                            fontSize: '14px',
                                            color: '#6B7280',
                                            textTransform: 'capitalize'
                                        }}>
                                            {section}
                                        </span>
                                        <span style={{
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#9334E9'
                                        }}>
                                            {data.score_percentage}%
                                        </span>
                                    </div>
                                    <ProgressBar percentage={data.score_percentage} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div>
                    {/* Email Notice Box - Desktop Only */}
                    {window.innerWidth > 768 && (
                        <div style={{
                            backgroundColor: '#FFFFFF',
                            padding: 'clamp(16px, 4vw, 24px)',
                            borderRadius: '16px',
                            border: '1px solid #F3F4F6',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                            marginBottom: 'clamp(20px, 5vw, 32px)',
                            textAlign: 'center'
                        }}>
                            <p style={{
                                fontSize: '14px',
                                color: '#374151',
                                margin: '0'
                            }}>
                                Check your inbox for a detailed analysis!
                            </p>
                        </div>
                    )}

                    {/* Weak Points Box */}
                    <div style={{
                        backgroundColor: '#FFFFFF',
                        padding: 'clamp(20px, 5vw, 32px)',
                        borderRadius: '16px',
                        border: '1px solid #F3F4F6',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                    }}>
                        <h3 style={{
                            fontSize: 'clamp(16px, 4vw, 18px)',
                            fontWeight: '500',
                            color: '#111827',
                            marginBottom: '8px',
                            margin: '0 0 8px 0'
                        }}>
                            Weak points
                        </h3>
                        <p style={{
                            fontSize: 'clamp(12px, 3vw, 14px)',
                            color: '#6B7280',
                            marginBottom: 'clamp(16px, 4vw, 24px)',
                            margin: '0 0 clamp(16px, 4vw, 24px) 0'
                        }}>
                            This graph highlights how you're performing in your weakest areas.
                        </p>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {diagnosticData.weak_points?.slice(0, 4).map((weakPoint, index) => (
                                <div key={index}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '8px'
                                    }}>
                                        <span style={{
                                            fontSize: '14px',
                                            color: '#6B7280'
                                        }}>
                                            {weakPoint.name}
                                        </span>
                                        <span style={{
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#9334E9'
                                        }}>
                                            {weakPoint.percentage}%
                                        </span>
                                    </div>
                                    <ProgressBar percentage={weakPoint.percentage} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
}

export default DiagnosticStats;